// SmartEditPreviewModal — Feb 24, 2026 (Randy pt89c-xxii, Phase 1).
// Displays the AI-generated edit plan (deletes / creates / modifies /
// marks_done), lets the user review + apply, then shows an in-modal
// success card with a 15-min undo. Reuses SmartCaptureModal's sticky-
// header, section-card style so the modal feels native.
import { useEffect, useMemo, useState } from "react";
import {
  X, MagicWand, Trash, Plus, PencilSimple, CheckCircle,
  Warning, ArrowClockwise, Question,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const POLL_MS = 1500;
const MAX_POLLS = 60;

function prettyDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
    });
  } catch { return iso; }
}
function pretty12(hhmm) {
  if (!hhmm) return "";
  const [H, M] = hhmm.split(":").map((s) => parseInt(s, 10));
  if (Number.isNaN(H)) return hhmm;
  const suf = H >= 12 ? "PM" : "AM";
  const h = H % 12 || 12;
  return `${h}:${String(M || 0).padStart(2, "0")} ${suf}`;
}

export default function SmartEditPreviewModal({ siloId, jobId, silo, onClose, onDone }) {
  const [status, setStatus] = useState("pending");
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [applyResult, setApplyResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const [undoSecondsLeft, setUndoSecondsLeft] = useState(15 * 60);

  useEffect(() => {
    let alive = true;
    let polls = 0;
    async function poll() {
      if (!alive) return;
      try {
        const r = await api.get(`/calendar/smart-edit/job/${jobId}`);
        const s = r.data?.status;
        setStatus(s);
        if (s === "preview_ready") { setPlan(r.data?.plan); return; }
        if (s === "error" || s === "empty") { setError(r.data?.error || "The AI couldn't build a plan."); return; }
        polls += 1;
        if (polls >= MAX_POLLS) { setError("This is taking longer than expected. Try again in a moment."); return; }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        setError(e.response?.data?.detail || "Couldn't check on the plan.");
      }
    }
    poll();
    return () => { alive = false; };
  }, [jobId]);

  // Countdown for the Undo window.
  useEffect(() => {
    if (!applyResult?.appliedAt) return undefined;
    const start = new Date(applyResult.appliedAt).getTime();
    const total = (applyResult.undoTtl || 15) * 60 * 1000;
    const tick = () => {
      const remaining = Math.max(0, Math.round((start + total - Date.now()) / 1000));
      setUndoSecondsLeft(remaining);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [applyResult?.appliedAt, applyResult?.undoTtl]);

  const doApply = async () => {
    setApplying(true);
    try {
      const r = await api.post(`/calendar/${siloId}/smart-edit/${jobId}/apply`);
      setApplyResult({
        summary: r.data?.summary,
        deleted: r.data?.deleted,
        created: r.data?.created,
        modified: r.data?.modified,
        markedDone: r.data?.marked_done,
        appliedAt: new Date().toISOString(),
        undoTtl: r.data?.undo_ttl_minutes || 15,
      });
      onDone?.();
      window.dispatchEvent(new Event("mysilo:smart-edit-applied"));
    } catch (e) {
      setError(e.response?.data?.detail || "Apply failed.");
    } finally {
      setApplying(false);
    }
  };

  const doUndo = async () => {
    setUndoing(true);
    try {
      await api.post(`/calendar/${siloId}/smart-edit/${jobId}/undo`);
      setApplyResult((r) => ({ ...(r || {}), undone: true }));
      onDone?.();
      window.dispatchEvent(new Event("mysilo:smart-edit-applied"));
    } catch (e) {
      setError(e.response?.data?.detail || "Undo failed.");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[240] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4" onClick={applying || undoing ? undefined : onClose}>
      <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-2xl border hairline" onClick={(e) => e.stopPropagation()} data-testid="smart-edit-preview-modal">
        <div className="px-5 py-3 border-b hairline flex items-center justify-between shrink-0">
          <h2 className="font-serif-display text-lg flex items-center gap-2">
            <MagicWand size={16} weight="duotone" className="text-[#8A6AC1]" />
            {applyResult ? "Edit applied" : "Smart Edit preview"}
          </h2>
          <button onClick={onClose} disabled={applying || undoing} className="p-1 rounded hover:bg-[#F0F1EE] disabled:opacity-40" data-testid="smart-edit-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-3 flex-1 overflow-y-auto text-sm">
          {status === "pending" && !applyResult && (
            <div className="text-[#6B6A68] italic py-6 text-center" data-testid="smart-edit-planning">
              Planning your edit… this usually takes 3-8 seconds.
            </div>
          )}
          {error && !applyResult && status === "empty" && (
            <div className="border border-[#C19A4A] bg-[#FDF9EF] text-[#6B4E1E] rounded-md p-3 text-xs" data-testid="smart-edit-empty">
              <Question size={13} weight="duotone" className="inline mr-1" /> {error}
            </div>
          )}
          {error && !applyResult && status !== "empty" && (
            <div className="border border-[#C16E5A] bg-[#FBEBE4] text-[#8A3A28] rounded-md p-3 text-xs" data-testid="smart-edit-error">
              <Warning size={13} weight="duotone" className="inline mr-1" /> {error}
            </div>
          )}
          {plan && !applyResult && (
            <>
              <PlanSummary plan={plan} />
              {(plan.changes && plan.changes.length > 0) ? (
                <div className="rounded-md border hairline bg-white" data-testid="smart-edit-changes">
                  <div className="px-3 py-2 border-b hairline text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
                    {plan.changes.length} change{plan.changes.length === 1 ? "" : "s"} to review
                  </div>
                  <div className="px-3 max-h-[46vh] overflow-y-auto">
                    {plan.changes.map((c, i) => <ChangeRow key={i} c={c} />)}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-[#6B6A68] italic" data-testid="smart-edit-nochanges">
                  {plan.summary || "Nothing to change."}
                </div>
              )}
            </>
          )}
          {applyResult && (
            <div className={`rounded-md border p-4 text-sm ${applyResult.undone ? "border-[#6B6A68] bg-[#F0F1EE] text-[#6B6A68]" : "border-[#4A9E6A] bg-[#E9F5EC] text-[#1F5D31]"}`} data-testid="smart-edit-applied-card">
              <div className="flex items-center gap-2 font-medium mb-1">
                <CheckCircle size={16} weight="duotone" /> {applyResult.undone ? "Reverted." : "Applied."}
              </div>
              <div className="text-xs leading-relaxed">
                {applyResult.summary || `${applyResult.deleted || 0} deleted · ${applyResult.created || 0} created · ${applyResult.modified || 0} modified · ${applyResult.markedDone || 0} marked done.`}
              </div>
              {!applyResult.undone && (
                <div className="mt-2 flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={doUndo}
                      disabled={undoing}
                      data-testid="smart-edit-undo"
                      className="text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md bg-[#1C1B1A] text-white hover:bg-black flex items-center gap-1 disabled:opacity-40"
                    >
                      <ArrowClockwise size={11} weight="bold" /> {undoing ? "Undoing…" : "Undo"}
                    </button>
                    <span className="text-[10px] text-[#4A5D4E]" data-testid="smart-edit-undo-timer">
                      Undo stays available for {Math.round((applyResult.undoTtl || 1440) / 60)}h
                    </span>
                  </div>
                  <span className="text-[10px] text-[#6B6A68] italic">
                    You can close this and still undo later — look for “Recent AI edits” by the Smart create button.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {plan && !applyResult && (
          <div className="px-5 py-3 border-t hairline flex items-center justify-end gap-2 shrink-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            <button onClick={onClose} disabled={applying} className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE] disabled:opacity-40" data-testid="smart-edit-cancel">Cancel</button>
            <button
              onClick={doApply}
              disabled={applying}
              data-testid="smart-edit-apply"
              className="text-xs px-4 py-2 rounded-md bg-[#8A6AC1] text-white hover:bg-[#7A5AB1] disabled:opacity-40 flex items-center gap-1.5"
            >
              {applying ? "Applying…" : `Apply ${(plan.deletes || []).length + (plan.creates || []).length + (plan.modifies || []).length + (plan.marks_done || []).length} change${((plan.deletes || []).length + (plan.creates || []).length + (plan.modifies || []).length + (plan.marks_done || []).length) === 1 ? "" : "s"}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PlanSummary({ plan }) {
  if (!plan?.summary) return null;
  return (
    <div className="text-sm text-[#1C1B1A] p-3 rounded-md bg-[#F8F5FC] border border-[#E9E2F2]" data-testid="smart-edit-summary">
      <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A6AC1] block mb-1">AI summary</span>
      {plan.summary}
    </div>
  );
}

function ChangeRow({ c }) {
  const ICON = {
    delete: <Trash size={14} weight="duotone" className="text-[#C16E5A] shrink-0 mt-0.5" />,
    modify: <PencilSimple size={14} weight="duotone" className="text-[#B8860B] shrink-0 mt-0.5" />,
    create: <Plus size={14} weight="duotone" className="text-[#4A9E6A] shrink-0 mt-0.5" />,
    done: <CheckCircle size={14} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />,
  }[c.action] || null;
  const stamp = (d, t) => `${prettyDate(d)}${t ? ` · ${pretty12(t)}` : ""}`;
  let detail;
  if (c.action === "modify") {
    const parts = [];
    if (c.to_date && c.to_date !== c.date) parts.push(`${prettyDate(c.date)} → ${prettyDate(c.to_date)}`);
    else parts.push(prettyDate(c.date));
    if (c.to_time) parts.push(`at ${pretty12(c.to_time)}`);
    if (c.to_assignee) parts.push(`→ ${c.to_assignee}`);
    detail = parts.join(" · ");
  } else {
    detail = stamp(c.date, c.time) + (c.assignee ? ` · ${c.assignee}` : "");
  }
  const tag = c.action === "modify" ? "move" : c.action;
  const tagCls = c.action === "delete" ? "bg-[#FBEBE4] text-[#8A3A28]"
    : c.action === "create" ? "bg-[#E9F5EC] text-[#1F5D31]"
    : c.action === "done" ? "bg-[#EAF0EB] text-[#4A5D4E]"
    : "bg-[#FBF3D9] text-[#6B4E1E]";
  return (
    <div className="flex items-start gap-2 py-2 border-b border-[#EFECE4] last:border-0" data-testid={`smart-edit-change-${c.action}`}>
      {ICON}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[#1C1B1A] leading-snug">{c.title}</div>
        <div className="text-[10px] text-[#6B6A68]">{detail}</div>
      </div>
      <span className={`text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded shrink-0 ${tagCls}`}>{tag}</span>
    </div>
  );
}
